import type { DnssecSummary, ResolverEvent } from "./api";
import type { RecordType } from "./recordTypes";

export type ReportRow = Omit<ResolverEvent, "state" | "status"> & {
  state: "ok" | "nxdomain" | "timeout" | "error" | "servfail";
};

export type DnsReport = {
  id: string;
  host: string;
  type: RecordType;
  createdAt: string;
  rows: ReportRow[];
  consensus: Record<string, number>;
  dnssec?: DnssecSummary;
  summary: {
    title: string;
    detail: string;
    agree: number;
    total: number;
    topAnswer: string | null;
    tone: "ok" | "warn" | "bad";
  };
};

export async function createReport(input: {
  host: string;
  type: RecordType;
  rows: ReportRow[];
  consensus: Record<string, number>;
  dnssec?: DnssecSummary | null;
}): Promise<{ report: DnsReport; url: string }> {
  const res = await fetch("/api/reports", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const body = await res.json() as { ok?: boolean; report?: DnsReport; url?: string; message?: string };
  if (!res.ok || !body.ok || !body.report || !body.url) {
    throw new Error(body.message ?? "Could not create report.");
  }
  return { report: body.report, url: body.url };
}

export async function fetchReport(id: string): Promise<DnsReport> {
  const res = await fetch(`/api/reports/${encodeURIComponent(id)}`);
  const body = await res.json() as { ok?: boolean; report?: DnsReport; message?: string };
  if (!res.ok || !body.ok || !body.report) {
    throw new Error(body.message ?? "Report not found.");
  }
  return body.report;
}
